import {
  ArrowCounterClockwise,
  ArrowLeft,
  ArrowsClockwise,
  CaretLeft,
  CaretRight,
  ChatCircle,
  Check,
  ImageSquare,
  PencilSimple,
  Sparkle,
  TextT,
  Warning,
} from '@phosphor-icons/react';
import {
  getListApprovalsQueryKey,
  useEditSlide,
  usePreviewSlide,
  useRerollSlide,
} from '@reelplicate/api-client/approvals';
import { useListMedia } from '@reelplicate/api-client/media';
import { useListMediaCollections } from '@reelplicate/api-client/media-collections';
import type {
  ApprovalDto,
  EditSlideDto,
  MediaAssetDto,
} from '@reelplicate/api-client/model';
import {
  Badge,
  Button,
  cn,
  IconButton,
  Modal,
  ModalContent,
  SearchField,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  TextArea,
} from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import {
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { CommentThread } from '@/features/comments/comments-drawer';

import type { RerollWhat } from '../editor/run/item-media';
import { ReelInspector } from './reel-editor/reel-inspector';
import { ReelStage } from './reel-editor/reel-stage';
import { usePlayback } from './reel-editor/use-playback';
import { useReelEditor } from './reel-editor/use-reel-editor';

export type ReviewTab = 'slides' | 'comments';

/** What review needs of a carousel slide. */
type Slide = {
  text?: string;
  textMarkup?: string;
  slot?: string;
  captionAssetId?: string;
  imageAssetId?: string;
  /** Made from slide pools: where it picks its image and caption from. */
  pool?: { images?: string; captions?: string };
  /** An AI background (Carousel slides). */
  image?: { prompt: string; assetId?: string };
};

type Picker = 'words' | 'image';

/** Wait after a pick before drawing its preview, so arrowing through the list stays light. */
const PREVIEW_DEBOUNCE_MS = 150;
/** Images in a search across collections at once; typing narrows them. */
const IMAGE_PICKS = 60;

/** Quiet panel swaps: they happen often, so they're short and only fade and nudge. */
const SWAP_IN =
  'motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-right-2 duration-180 ease-out';

/** The edit that puts a slide back as it was. */
const originalOf = (slide: Slide): EditSlideDto => ({
  ...(slide.captionAssetId
    ? { captionAssetId: slide.captionAssetId }
    : { text: slide.textMarkup ?? slide.text ?? '' }),
  ...(slide.imageAssetId || slide.image?.assetId
    ? { imageAssetId: slide.imageAssetId ?? slide.image?.assetId }
    : {}),
});

/**
 * A draft in review. The slide owns the stage, a filmstrip below says where
 * you are and what needs a look, and the inspector shows what the current
 * slide is - its words and its image - each with Change. Changing opens a
 * picker; a pick is previewed on the stage, drawn exactly as it would be, and
 * kept only on Apply. Edits can be undone. Approve and Reject close it.
 * A waiting reel opens in the reel editor instead: cut it, add text and
 * subtitles, and save a new version before approving.
 */
export function DraftReviewModal({
  workspaceId,
  approval,
  slide,
  onSlideChange,
  tab,
  onTabChange,
  busy,
  onDecide,
  reject,
  onClose,
}: {
  workspaceId: string;
  approval: ApprovalDto;
  slide: number;
  onSlideChange: (slide: number) => void;
  tab: ReviewTab;
  onTabChange: (tab: ReviewTab) => void;
  busy: boolean;
  onDecide: (decision: 'APPROVE' | 'REJECT', note?: string) => void;
  /** The Reject button with its note (shared with the queue). */
  reject: ReactNode;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { item } = approval;
  const images = item.media
    .filter((file) => file.kind === 'image')
    .map((file) => file.url);
  const slides = Array.isArray(item.payload.slides)
    ? (item.payload.slides as Slide[])
    : [];
  const isCarousel = item.dataType === 'carousel' && slides.length > 0;
  const count = Math.max(images.length, 1);
  const current = Math.min(slide, count - 1);
  const currentSlide = slides[current];
  const caption = typeof item.payload.caption === 'string' ? item.payload.caption : null;
  const pending = approval.status === 'PENDING';
  const width = typeof item.payload.width === 'number' ? item.payload.width : 4;
  const height = typeof item.payload.height === 'number' ? item.payload.height : 5;

  // A reel: edited while it waits, played once it's reviewed.
  const isReel = item.dataType === 'reel';
  const video = item.media.find((file) => file.kind === 'video')?.url;
  const reelEditor = useReelEditor({
    workspaceId,
    approvalId: approval.id,
    enabled: isReel && pending,
  });
  const playback = usePlayback(reelEditor.state.timeline);
  const editingReel = isReel && pending && Boolean(reelEditor.saved);
  const reelBlocked = editingReel && (reelEditor.rendering || reelEditor.dirty);

  // Review progress: slides looked at, and slides changed here (with how they were).
  const [seen, setSeen] = useState<Set<number>>(() => new Set([current]));
  const [originals, setOriginals] = useState<Map<number, EditSlideDto>>(new Map());

  const [picker, setPicker] = useState<Picker | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);

  /** Another slide: it counts as seen, and starts fresh - no picker, no preview. */
  const go = (next: number) => {
    setSeen((prev) => (prev.has(next) ? prev : new Set(prev).add(next)));
    setPicker(null);
    setPreviewUrl(null);
    onSlideChange(next);
  };

  const edit = useEditSlide();
  const reroll = useRerollSlide();
  const working = edit.isPending || reroll.isPending;

  const show = (updated: ApprovalDto) =>
    queryClient.setQueryData<ApprovalDto[]>(
      getListApprovalsQueryKey(workspaceId, { status: 'PENDING' }),
      (all) => all?.map((a) => (a.id === updated.id ? updated : a)),
    );

  const remember = () => {
    if (currentSlide && !originals.has(current)) {
      setOriginals(new Map(originals).set(current, originalOf(currentSlide)));
    }
  };

  const apply = async (data: EditSlideDto) => {
    remember();
    try {
      show(
        await edit.mutateAsync({
          id: workspaceId,
          approvalId: approval.id,
          slide: current,
          data,
        }),
      );
      setPicker(null);
      setPreviewUrl(null);
    } catch {
      // Shown as a toast.
    }
  };

  const shuffle = async (what: RerollWhat) => {
    remember();
    try {
      show(
        await reroll.mutateAsync({
          id: workspaceId,
          approvalId: approval.id,
          data: { slide: current, what },
        }),
      );
    } catch {
      // Shown as a toast.
    }
  };

  const undo = async () => {
    const original = originals.get(current);
    if (!original) return;
    await apply(original);
    const next = new Map(originals);
    next.delete(current);
    setOriginals(next);
  };

  const noText = isCarousel ? slides.filter((s) => !s.text?.trim()).length : 0;
  const step = (by: number) => {
    const next = current + by;
    if (next >= 0 && next < count) go(next);
  };
  // ← / → step through slides, unless typing or picking.
  const onKeyDown = (event: KeyboardEvent) => {
    const target = event.target as HTMLElement;
    if (picker || target.closest('input, textarea, [role="listbox"], [role="tablist"]'))
      return;
    if (event.key === 'ArrowLeft') step(-1);
    if (event.key === 'ArrowRight') step(1);
  };

  return (
    <Modal open onOpenChange={(open) => !open && onClose()}>
      <ModalContent
        size="xl"
        // A reel edits full screen on a phone.
        className={cn(
          isReel &&
            'max-tablet:h-dvh max-tablet:w-screen max-tablet:max-w-none max-tablet:rounded-none',
        )}
        title={item.summary}
        description={[approval.flowName, approval.runMode === 'TEST' ? 'Test run' : null]
          .filter(Boolean)
          .join(' · ')}
        onKeyDown={onKeyDown}
        // Esc closes a picker before it closes the review.
        onEscapeKeyDown={(event) => {
          if (picker) {
            event.preventDefault();
            setPicker(null);
            setPreviewUrl(null);
          }
        }}
        footerStart={
          isCarousel ? (
            <ReviewSummary noText={noText} seen={seen.size} total={count} />
          ) : (
            reelBlocked && (
              <span className="text-body-sm text-text-secondary">
                {reelEditor.rendering
                  ? 'Approve once your edit has rendered.'
                  : 'Save or discard your edit to approve.'}
              </span>
            )
          )
        }
        footer={
          pending ? (
            <>
              {reject}
              <Button
                leadingIcon={Check}
                disabled={busy || working || reelBlocked}
                onClick={() => onDecide('APPROVE')}
              >
                {noText > 0 ? 'Approve anyway' : 'Approve'}
              </Button>
            </>
          ) : (
            <Badge tone={approval.status === 'APPROVED' ? 'success' : 'danger'} dot>
              {approval.status === 'APPROVED' ? 'Approved' : 'Rejected'}
            </Badge>
          )
        }
      >
        <div className="flex h-full min-h-0 gap-6">
          {/* Stage: the slide, then the filmstrip - or the reel editor. */}
          <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
            {editingReel ? (
              <ReelStage editor={reelEditor} playback={playback} />
            ) : (
              <div className="bg-bg-subtle relative flex min-h-0 flex-1 items-center justify-center rounded-lg p-4">
                {isReel && video ? (
                  <video
                    src={video}
                    controls
                    playsInline
                    aria-label="Reel"
                    style={{ aspectRatio: `${width} / ${height}` }}
                    className="max-h-full max-w-full rounded-md bg-black shadow-sm"
                  />
                ) : images.length > 0 ? (
                  <img
                    key={previewUrl ?? images[current]}
                    src={previewUrl ?? images[current]}
                    alt={`Slide ${current + 1}${currentSlide?.text ? `: ${currentSlide.text}` : ''}`}
                    style={{ aspectRatio: `${width} / ${height}` }}
                    className="motion-safe:animate-in motion-safe:fade-in-0 max-h-full max-w-full rounded-md object-contain shadow-sm duration-200 ease-out"
                  />
                ) : (
                  <p className="text-body-md text-text-secondary">Nothing to preview.</p>
                )}
                {isCarousel && (
                  <span className="text-caption absolute top-3 left-3 rounded-full bg-black/60 px-2.5 py-1 text-white tabular-nums">
                    {current + 1} / {count}
                    {currentSlide?.slot ? ` · ${currentSlide.slot}` : ''}
                  </span>
                )}
                {previewUrl && (
                  <Badge tone="brand" className="absolute top-3 right-3">
                    Preview
                  </Badge>
                )}
                {(previewing || working) && (
                  <span
                    role="status"
                    className="text-caption absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-2.5 py-1 text-white"
                  >
                    {working ? 'Saving the slide…' : 'Drawing the preview…'}
                  </span>
                )}
                {count > 1 && (
                  <>
                    <StageArrow
                      side="left"
                      hidden={current === 0}
                      onClick={() => step(-1)}
                    />
                    <StageArrow
                      side="right"
                      hidden={current === count - 1}
                      onClick={() => step(1)}
                    />
                  </>
                )}
              </div>
            )}
            {isCarousel && (
              <Filmstrip
                images={images}
                slides={slides}
                current={current}
                seen={seen}
                edited={originals}
                onSelect={go}
              />
            )}
          </div>

          {/* Inspector: this slide (or its picker), and the comments. */}
          <Tabs
            value={tab}
            onValueChange={(value) => onTabChange(value as ReviewTab)}
            className={cn(
              'flex min-h-0 w-[360px] shrink-0 flex-col gap-4',
              isReel && 'max-tablet:hidden',
            )}
          >
            <TabList aria-label="Review">
              <Tab value="slides">
                {isCarousel ? 'This slide' : editingReel ? 'Edit' : 'Copy'}
              </Tab>
              <Tab value="comments" icon={ChatCircle} count={approval.commentCount}>
                Comments
              </Tab>
            </TabList>
            <TabPanel value="slides" className="flex min-h-0 flex-1 flex-col">
              {isCarousel && currentSlide && picker && pending ? (
                <SlidePicker
                  key={`${current}-${picker}`}
                  kind={picker}
                  workspaceId={workspaceId}
                  approvalId={approval.id}
                  index={current}
                  slide={currentSlide}
                  saving={working}
                  onPreview={setPreviewUrl}
                  onPreviewing={setPreviewing}
                  onApply={(data) => void apply(data)}
                  onCancel={() => {
                    setPicker(null);
                    setPreviewUrl(null);
                  }}
                />
              ) : (
                <div
                  className={cn('flex min-h-0 flex-col gap-5 overflow-y-auto', SWAP_IN)}
                >
                  {isCarousel && currentSlide && (
                    <SlideInspector
                      workspaceId={workspaceId}
                      slide={currentSlide}
                      editable={pending}
                      working={working}
                      edited={originals.has(current)}
                      onChange={setPicker}
                      onRemoveText={() => void apply({ text: '' })}
                      onShuffle={(what) => void shuffle(what)}
                      onUndo={() => void undo()}
                    />
                  )}
                  {editingReel && reelEditor.saved ? (
                    <ReelInspector
                      workspaceId={workspaceId}
                      timeline={reelEditor.state.timeline}
                      selected={reelEditor.state.selected}
                      hasSubtitles={reelEditor.saved.hasSubtitles}
                      hasWords={reelEditor.saved.words.length > 0}
                      versions={reelEditor.saved.versions}
                      liveEditId={reelEditor.saved.liveEditId}
                      busy={reelEditor.rendering || reelEditor.restoring}
                      dispatch={reelEditor.dispatch}
                      onAddText={() => reelEditor.addText(playback.time)}
                      onRestore={(editId) => void reelEditor.restore(editId)}
                    >
                      {caption && <PostCaption caption={caption} divided />}
                    </ReelInspector>
                  ) : (
                    caption && <PostCaption caption={caption} divided={isCarousel} />
                  )}
                </div>
              )}
            </TabPanel>
            <TabPanel value="comments" className="min-h-0 flex-1 overflow-y-auto">
              <CommentThread workspaceId={workspaceId} approvalId={approval.id} />
            </TabPanel>
          </Tabs>
        </div>
      </ModalContent>
    </Modal>
  );
}

function PostCaption({ caption, divided }: { caption: string; divided: boolean }) {
  return (
    <section
      aria-label="Post caption"
      className={cn(
        'flex flex-col gap-1.5',
        divided && 'border-border-subtle border-t pt-5',
      )}
    >
      <h3 className="text-label-md text-text-primary">Post caption</h3>
      <p className="text-body-sm text-text-secondary whitespace-pre-line">{caption}</p>
    </section>
  );
}

function StageArrow({
  side,
  hidden,
  onClick,
}: {
  side: 'left' | 'right';
  hidden: boolean;
  onClick: () => void;
}) {
  if (hidden) return null;
  const Icon = side === 'left' ? CaretLeft : CaretRight;
  return (
    <button
      type="button"
      aria-label={side === 'left' ? 'Previous slide' : 'Next slide'}
      onClick={onClick}
      className={cn(
        'bg-bg-surface/90 focus-visible:shadow-focus-ring hover:bg-bg-surface absolute top-1/2 flex size-9 -translate-y-1/2 items-center justify-center rounded-full shadow-sm outline-none transition-transform duration-150 active:scale-[0.97]',
        side === 'left' ? 'left-3' : 'right-3',
      )}
    >
      <Icon aria-hidden className="text-text-primary size-5" />
    </button>
  );
}

/** "1 slide has no text" while something needs a look; otherwise how far the check got. */
function ReviewSummary({
  noText,
  seen,
  total,
}: {
  noText: number;
  seen: number;
  total: number;
}) {
  if (noText > 0) {
    return (
      <span className="text-body-sm text-status-warning-fg inline-flex items-center gap-1.5">
        <Warning aria-hidden className="size-4 shrink-0" />
        {noText === 1 ? '1 slide has no text' : `${noText} slides have no text`}
      </span>
    );
  }
  return (
    <span className="text-body-sm text-text-secondary inline-flex items-center gap-1.5">
      {seen >= total && <Check aria-hidden className="text-status-success-fg size-4" />}
      {seen >= total
        ? `All ${total} slides checked`
        : `Checked ${seen} of ${total} slides`}
    </span>
  );
}

/** The slides small: where you are, which have no text, which you changed, which you've seen. */
function Filmstrip({
  images,
  slides,
  current,
  seen,
  edited,
  onSelect,
}: {
  images: string[];
  slides: Slide[];
  current: number;
  seen: Set<number>;
  edited: Map<number, EditSlideDto>;
  onSelect: (slide: number) => void;
}) {
  return (
    <ol aria-label="Slides" className="flex shrink-0 gap-2 overflow-x-auto p-1">
      {images.map((url, i) => {
        const s = slides[i];
        const empty = !s?.text?.trim();
        const label = [
          `Slide ${i + 1}`,
          s?.slot,
          empty ? 'no text' : null,
          edited.has(i) ? 'edited' : null,
        ]
          .filter(Boolean)
          .join(', ');
        return (
          <li key={i} className="shrink-0">
            <button
              type="button"
              aria-label={label}
              aria-current={i === current || undefined}
              onClick={() => onSelect(i)}
              className={cn(
                'focus-visible:shadow-focus-ring relative block h-20 overflow-hidden rounded-md outline-none transition-[box-shadow,opacity] duration-150 ease-out',
                i === current
                  ? 'ring-border-brand ring-2 ring-offset-2'
                  : seen.has(i)
                    ? 'opacity-90 hover:opacity-100'
                    : 'opacity-70 hover:opacity-100',
              )}
            >
              <img src={url} alt="" className="h-full w-auto object-cover" />
              {empty && (
                <span
                  aria-hidden
                  title="No text"
                  className="bg-status-warning-solid motion-safe:animate-in motion-safe:zoom-in-90 absolute top-1 right-1 size-2.5 rounded-full ring-2 ring-white"
                />
              )}
              {edited.has(i) && (
                <span
                  aria-hidden
                  className="bg-brand-strong motion-safe:animate-in motion-safe:zoom-in-90 absolute bottom-1 left-1 flex size-4 items-center justify-center rounded-full text-white ring-2 ring-white"
                >
                  <PencilSimple className="size-2.5" weight="bold" />
                </span>
              )}
            </button>
          </li>
        );
      })}
    </ol>
  );
}

/** What the slide is now - its words and its image - each with Change. */
function SlideInspector({
  workspaceId,
  slide,
  editable,
  working,
  edited,
  onChange,
  onRemoveText,
  onShuffle,
  onUndo,
}: {
  workspaceId: string;
  slide: Slide;
  editable: boolean;
  working: boolean;
  edited: boolean;
  onChange: (picker: Picker) => void;
  onRemoveText: () => void;
  onShuffle: (what: RerollWhat) => void;
  onUndo: () => void;
}) {
  const { data: library } = useListMedia(workspaceId, {
    query: { enabled: Boolean(workspaceId) },
  });
  const imageId = slide.imageAssetId ?? slide.image?.assetId;
  const image = library?.items.find((asset) => asset.id === imageId);
  const text = slide.text?.trim();

  return (
    <>
      <section aria-label="Words" className="flex flex-col gap-2">
        <InspectorHeading icon={TextT} title="Words">
          {editable && (
            <>
              {text && (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={working}
                  onClick={onRemoveText}
                >
                  Remove
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                disabled={working}
                aria-label="Change words"
                onClick={() => onChange('words')}
              >
                Change
              </Button>
            </>
          )}
        </InspectorHeading>
        {text ? (
          <p className="bg-bg-subtle text-body-md text-text-primary rounded-md px-3 py-2.5 whitespace-pre-line">
            {slide.text}
          </p>
        ) : (
          <p className="bg-status-warning-bg text-body-sm text-status-warning-fg flex items-start gap-2 rounded-md px-3 py-2.5">
            <Warning aria-hidden className="mt-0.5 size-4 shrink-0" />
            No text on this slide.
          </p>
        )}
        {editable && slide.pool?.captions && (
          <ShuffleLink disabled={working} onClick={() => onShuffle('caption')}>
            Try another caption from the pool
          </ShuffleLink>
        )}
      </section>

      <section aria-label="Image" className="flex flex-col gap-2">
        <InspectorHeading icon={ImageSquare} title="Image">
          {editable && (
            <Button
              size="sm"
              variant="outline"
              disabled={working}
              aria-label="Change image"
              onClick={() => onChange('image')}
            >
              Change
            </Button>
          )}
        </InspectorHeading>
        <div className="flex items-center gap-3">
          {image?.thumbnailUrl ? (
            <img
              src={image.thumbnailUrl}
              alt=""
              className="bg-bg-subtle h-14 w-11 shrink-0 rounded-sm object-cover"
            />
          ) : (
            <span className="bg-bg-subtle flex h-14 w-11 shrink-0 items-center justify-center rounded-sm">
              <ImageSquare aria-hidden className="text-icon-tertiary size-5" />
            </span>
          )}
          <span className="text-body-sm text-text-secondary min-w-0">
            <span className="text-text-primary block truncate">
              {image?.title ?? (slide.image ? 'AI image' : 'Image from the step before')}
            </span>
            {slide.pool?.images
              ? 'From this slide’s pool'
              : slide.image
                ? 'Made for this slide'
                : null}
          </span>
        </div>
        {editable && slide.pool?.images && (
          <ShuffleLink disabled={working} onClick={() => onShuffle('image')}>
            Try another image from the pool
          </ShuffleLink>
        )}
        {editable && !slide.pool && (
          <ShuffleLink
            disabled={working}
            onClick={() => onShuffle('aiImage')}
            icon={Sparkle}
          >
            {slide.image ? 'New AI image' : 'Add an AI image'}
          </ShuffleLink>
        )}
      </section>

      {editable && edited && (
        <Button
          size="sm"
          variant="ghost"
          leadingIcon={ArrowCounterClockwise}
          disabled={working}
          onClick={onUndo}
          className="self-start"
        >
          Undo changes to this slide
        </Button>
      )}
    </>
  );
}

function InspectorHeading({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof TextT;
  title: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex min-h-8 items-center gap-2">
      <Icon aria-hidden className="text-icon-secondary size-4" />
      <h3 className="text-label-md text-text-primary flex-1">{title}</h3>
      {children}
    </div>
  );
}

function ShuffleLink({
  icon: Icon = ArrowsClockwise,
  disabled,
  onClick,
  children,
}: {
  icon?: typeof ArrowsClockwise;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="text-caption text-text-secondary hover:text-text-primary focus-visible:shadow-focus-ring inline-flex items-center gap-1.5 self-start rounded-xs outline-none disabled:opacity-50"
    >
      <Icon aria-hidden className="size-3.5" />
      {children}
    </button>
  );
}

/** The folder for picks in no collection. */
const NO_COLLECTION = 'none';

/**
 * Change the words or the image, browsed by collection: pick one, then its
 * captions or images (the slide's pool opens straight away, the current
 * choice marked); search looks across all of them. A pick is drawn on the stage as a preview; Apply keeps it, Cancel (or
 * Esc) doesn't. Enter in "Write your own" applies.
 */
function SlidePicker({
  kind,
  workspaceId,
  approvalId,
  index,
  slide,
  saving,
  onPreview,
  onPreviewing,
  onApply,
  onCancel,
}: {
  kind: Picker;
  workspaceId: string;
  approvalId: string;
  index: number;
  slide: Slide;
  saving: boolean;
  onPreview: (url: string | null) => void;
  onPreviewing: (busy: boolean) => void;
  onApply: (edit: EditSlideDto) => void;
  onCancel: () => void;
}) {
  const enabled = { query: { enabled: Boolean(workspaceId) } };
  const { data: library } = useListMedia(workspaceId, enabled);
  const { data: collections } = useListMediaCollections(workspaceId, enabled);
  const preview = usePreviewSlide();
  const pool = kind === 'words' ? slide.pool?.captions : slide.pool?.images;
  const [query, setQuery] = useState('');
  // The collection whose picks are showing; null lists the collections.
  const [folder, setFolder] = useState<string | null>(pool ?? null);
  const [choice, setChoice] = useState<EditSlideDto | null>(null);
  const [writing, setWriting] = useState(false);
  const [words, setWords] = useState('');
  const cache = useRef(new Map<string, string>());

  const nameOf = (asset: MediaAssetDto) =>
    asset.collectionIds
      .map((id) => collections?.find((c) => c.id === id)?.name)
      .find(Boolean);
  const inFolder = (asset: MediaAssetDto, id: string) =>
    id === NO_COLLECTION
      ? !asset.collectionIds.some((c) => collections?.some((known) => known.id === c))
      : asset.collectionIds.includes(id);

  const assets = useMemo(
    () =>
      (library?.items ?? []).filter((asset) =>
        kind === 'words'
          ? asset.kind === 'CAPTION' && asset.caption
          : asset.kind === 'IMAGE' && asset.thumbnailUrl,
      ),
    [library, kind],
  );

  // Collections holding picks, the slide's pool first; then the loose ones.
  const folders = useMemo(() => {
    const rowOf = (id: string, name: string) => {
      const items = assets.filter((a) => inFolder(a, id));
      return { id, name, count: items.length, cover: items[0]?.thumbnailUrl };
    };
    const rows = (collections ?? [])
      .map((c) => rowOf(c.id, c.name))
      .filter((row) => row.count > 0)
      .sort((a, b) => Number(b.id === pool) - Number(a.id === pool));
    const loose = rowOf(
      NO_COLLECTION,
      kind === 'words' ? 'Captions not in a collection' : 'Images not in a collection',
    );
    return loose.count ? [...rows, loose] : rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assets, collections, kind, pool]);

  const q = query.trim().toLowerCase();
  const browsing = folder === null && !q;
  const folderName =
    folder === NO_COLLECTION
      ? 'Not in a collection'
      : collections?.find((c) => c.id === folder)?.name;

  const options = useMemo(() => {
    const matches = assets.filter(
      (asset) =>
        !q ||
        [asset.title, asset.caption ?? '', ...asset.tags, nameOf(asset) ?? ''].some((t) =>
          t.toLowerCase().includes(q),
        ),
    );
    if (folder !== null) {
      return { own: [], rest: matches.filter((a) => inFolder(a, folder)) };
    }
    const own = pool ? matches.filter((a) => a.collectionIds.includes(pool)) : [];
    const rest = matches.filter((a) => !own.includes(a));
    return { own, rest: kind === 'image' ? rest.slice(0, IMAGE_PICKS) : rest };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assets, collections, q, kind, pool, folder]);

  const currentId = kind === 'words' ? slide.captionAssetId : slide.imageAssetId;

  // A pick is drawn on the stage once picking pauses; drawn picks are kept.
  const { mutateAsync } = preview;
  useEffect(() => {
    if (!choice) return;
    const key = JSON.stringify(choice);
    const known = cache.current.get(key);
    if (known) {
      onPreview(known);
      return;
    }
    let live = true;
    const timer = setTimeout(() => {
      onPreviewing(true);
      mutateAsync({ id: workspaceId, approvalId, slide: index, data: choice })
        .then(({ url }) => {
          cache.current.set(key, url);
          if (live) onPreview(url);
        })
        .catch(() => {
          // Shown as a toast.
        })
        .finally(() => onPreviewing(false));
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [choice, workspaceId, approvalId, index, mutateAsync, onPreview, onPreviewing]);

  const pick = (id: string) =>
    setChoice(kind === 'words' ? { captionAssetId: id } : { imageAssetId: id });
  const picked = choice?.captionAssetId ?? choice?.imageAssetId;

  // ↑ / ↓ move through the options and preview each.
  const ids = [...options.own, ...options.rest].map((a) => a.id);
  const onListKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const at = picked ? ids.indexOf(picked) : -1;
    const next =
      ids[
        Math.min(Math.max(at + (event.key === 'ArrowDown' ? 1 : -1), 0), ids.length - 1)
      ];
    if (next) {
      pick(next);
      document.getElementById(`pick-${next}`)?.focus();
    }
  };

  const inside = folder !== null;
  const title = inside
    ? (folderName ?? 'Collection')
    : kind === 'words'
      ? 'Change words'
      : 'Change image';
  const empty = browsing
    ? folders.length === 0
    : options.own.length + options.rest.length === 0;

  return (
    <section
      aria-label={kind === 'words' ? 'Change words' : 'Change image'}
      className={cn('flex min-h-0 flex-1 flex-col gap-3', SWAP_IN)}
    >
      <div className="flex items-center gap-1">
        <IconButton
          icon={ArrowLeft}
          size="sm"
          aria-label={inside ? 'Back to collections' : 'Back to the slide'}
          onClick={() => (inside ? setFolder(null) : onCancel())}
        />
        <h3 className="text-label-md text-text-primary min-w-0 truncate">{title}</h3>
      </div>
      <SearchField
        aria-label={kind === 'words' ? 'Search captions' : 'Search images'}
        placeholder={
          inside
            ? `Search ${folderName ?? 'this collection'}`
            : kind === 'words'
              ? 'Search captions'
              : 'Search images'
        }
        value={query}
        onValueChange={setQuery}
        shortcut={false}
      />

      {kind === 'words' && (
        <div className="border-border-subtle shrink-0 rounded-md border">
          {writing ? (
            <div className="flex flex-col gap-2 p-2">
              <TextArea
                aria-label="Your words"
                autoFocus
                value={words}
                placeholder="What should this slide say?"
                onChange={(event) => setWords(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey && words.trim()) {
                    event.preventDefault();
                    onApply({ text: words.trim() });
                  }
                }}
              />
              <div className="flex items-center gap-2">
                <span className="text-caption text-text-tertiary flex-1">
                  Enter to apply · Shift+Enter for a new line
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={!words.trim()}
                  onClick={() => setChoice({ text: words.trim() })}
                >
                  Preview
                </Button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setWriting(true)}
              className="text-body-sm text-text-primary hover:bg-bg-muted focus-visible:shadow-focus-ring flex w-full items-center gap-2 rounded-md px-3 py-2.5 text-left outline-none"
            >
              <PencilSimple aria-hidden className="text-icon-secondary size-4" />
              Write your own
            </button>
          )}
        </div>
      )}

      <div className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1 pt-1">
        {empty ? (
          <p className="text-body-sm text-text-secondary py-6 text-center">
            {query
              ? `Nothing matches “${query}”.`
              : inside
                ? `No ${kind === 'words' ? 'captions' : 'images'} in this collection.`
                : kind === 'words'
                  ? 'No captions in your library yet.'
                  : 'No images in your library yet.'}
          </p>
        ) : browsing ? (
          <div className="flex flex-col gap-1.5">
            <span className="text-caption text-text-tertiary px-1">Collections</span>
            <ul aria-label="Collections" className="flex flex-col gap-1">
              {folders.map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    onClick={() => setFolder(row.id)}
                    className="hover:bg-bg-muted focus-visible:shadow-focus-ring flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left outline-none"
                  >
                    {kind === 'image' &&
                      (row.cover ? (
                        <img
                          src={row.cover}
                          alt=""
                          className="bg-bg-subtle h-10 w-8 shrink-0 rounded-sm object-cover"
                        />
                      ) : (
                        <span className="bg-bg-subtle h-10 w-8 shrink-0 rounded-sm" />
                      ))}
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className="text-body-sm text-text-primary truncate">
                        {row.name}
                      </span>
                      {row.id === pool && (
                        <span className="text-caption text-text-tertiary">
                          This slide’s pool
                        </span>
                      )}
                    </span>
                    <span className="text-caption text-text-tertiary tabular-nums">
                      {row.count}
                    </span>
                    <CaretRight aria-hidden className="text-icon-tertiary size-4" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div
            role="listbox"
            aria-label={kind === 'words' ? 'Captions' : 'Images'}
            tabIndex={-1}
            onKeyDown={onListKeyDown}
            className="flex flex-col gap-4"
          >
            {[
              { label: 'From this slide’s pool', items: options.own },
              {
                label: inside
                  ? ''
                  : options.own.length
                    ? 'Everything else'
                    : kind === 'words'
                      ? 'All captions'
                      : 'All images',
                items: options.rest,
              },
            ]
              .filter((group) => group.items.length > 0)
              .map((group) => (
                <div
                  key={group.label || 'picks'}
                  role="group"
                  aria-label={group.label || title}
                  className="flex flex-col gap-1.5"
                >
                  {group.label && (
                    <span className="text-caption text-text-tertiary px-1">
                      {group.label}
                    </span>
                  )}
                  <div
                    className={
                      kind === 'image' ? 'grid grid-cols-3 gap-2' : 'flex flex-col gap-1'
                    }
                  >
                    {group.items.map((asset) => {
                      const selected = picked === asset.id;
                      const onSlide = currentId === asset.id;
                      const note = onSlide
                        ? 'On the slide'
                        : inside
                          ? null
                          : (nameOf(asset) ?? asset.title);
                      return kind === 'words' ? (
                        <button
                          key={asset.id}
                          id={`pick-${asset.id}`}
                          type="button"
                          role="option"
                          aria-selected={selected}
                          onClick={() => pick(asset.id)}
                          className={cn(
                            'focus-visible:shadow-focus-ring flex flex-col gap-0.5 rounded-md px-3 py-2 text-left outline-none',
                            selected ? 'bg-brand-subtle' : 'hover:bg-bg-muted',
                          )}
                        >
                          <span className="text-body-sm text-text-primary line-clamp-2">
                            {asset.caption}
                          </span>
                          {note && (
                            <span className="text-caption text-text-tertiary">
                              {note}
                            </span>
                          )}
                        </button>
                      ) : (
                        <button
                          key={asset.id}
                          id={`pick-${asset.id}`}
                          type="button"
                          role="option"
                          aria-selected={selected}
                          aria-label={`${asset.title}${onSlide ? ', on the slide' : ''}`}
                          title={asset.title}
                          onClick={() => pick(asset.id)}
                          className={cn(
                            'focus-visible:shadow-focus-ring relative block aspect-[4/5] overflow-hidden rounded-md outline-none',
                            selected && 'ring-border-brand ring-2 ring-offset-2',
                          )}
                        >
                          <img
                            src={asset.thumbnailUrl!}
                            alt=""
                            loading="lazy"
                            className="size-full object-cover"
                          />
                          {onSlide && (
                            <span className="text-caption absolute inset-x-1 bottom-1 rounded-sm bg-black/60 px-1 py-0.5 text-center text-white">
                              On the slide
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      <div className="border-border-subtle flex items-center justify-end gap-2 border-t pt-3">
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          size="sm"
          disabled={!choice || saving}
          loading={saving}
          onClick={() => choice && onApply(choice)}
        >
          Apply
        </Button>
      </div>
    </section>
  );
}
